import React, { FC, useEffect, useState } from 'react';
import History from 'src/components/History';
import { getCodesObject } from 'src/resources/constantsCodes/functions';
import { ConstantsCodesObjectType } from 'src/types/constantsCodes';
import { OrderChangelogType } from 'src/types/orderDetail';
import getCountryOptions from 'src/utils/countryOptions';
import { historyOrderFileUpdates, historyOrderPalletUpdates, historyOrderProductFileUpdates, historyProductSetsUpdates, historyProductUpdates, historySlaCalculationUpdates, historyUpdates, historyUpdatesSkladonUser, historyCreateHiddenKeys, slaReasonAttributeKeys } from './constants';
import { ChangelogUpdateType } from 'src/components/History/types';
import { OrderProductSetWithProductType } from 'src/types/order';
import { RootStoreType } from '../../types/store';
import { useSelector } from 'react-redux';
import translate from '@translate';
import { resolveHistoryMessage } from './resolveHistoryMessage';
import moment from 'moment';

const translateBoolean = (value: boolean | string | null | undefined): string => {
    if (value === true || value === 'true') return translate('utils.boolCodes.yes');
    if (value === false || value === 'false') return translate('utils.boolCodes.no');
    return '';
};

// Filter SLA_CALCULATION data to only include relevant attributes for the given reason
const filterSlaDataByReason = (slaData: any, reason: string): any => {
    const allowedKeys = slaReasonAttributeKeys[reason];
    if (!allowedKeys) {
        // Fallback - show all attributes
        return slaData;
    }

    const orderType = slaData?.input?.orderType?.toLowerCase();
    const isB2C = orderType === 'b2c';

    const filteredData: any = {};

    // Helper to check if a key is allowed
    const isKeyAllowed = (fullKey: string): boolean => {
        // For B2C, filter out productsCount
        if (isB2C && fullKey === 'SLA_CALCULATION.input.productsCount') {
            return false;
        }
        return allowedKeys.includes(fullKey);
    };

    // Copy only allowed top-level keys
    // For result and previousSlaDate, always show them if allowed (even if null - formatDate will display as '-')
    if (isKeyAllowed('SLA_CALCULATION.result')) {
        filteredData.result = slaData.result;
    }
    if (isKeyAllowed('SLA_CALCULATION.previousSlaDate')) {
        filteredData.previousSlaDate = slaData.previousSlaDate;
    }

    // Filter input object
    if (slaData.input) {
        const filteredInput: any = {};
        const inputKeys = [
            'orderType', 'shipper', 'providerCot', 'carrierCot', 'productsCount', 
            'postponeDays', 'requiredExpeditionDate', 'orderTimestamp',
            'b2bD0', 'b2bD1', 'b2bD2', 'b2bD3',
            'waitingForPaymentAt', 'suspendedAt', 'productsNotAvailableAt', 'incorrectAt'
        ];
        // Keys that should always be shown even if empty/null
        const alwaysShowKeys = [
            'providerCot', 'carrierCot', 'orderTimestamp', 'requiredExpeditionDate',
            'waitingForPaymentAt', 'suspendedAt', 'productsNotAvailableAt', 'incorrectAt'
        ];
        inputKeys.forEach(key => {
            const fullKey = `SLA_CALCULATION.input.${key}`;
            const shouldAlwaysShow = alwaysShowKeys.includes(key);
            if (isKeyAllowed(fullKey)) {
                if (shouldAlwaysShow) {
                    // Always show this key, use '-' for empty values
                    filteredInput[key] = slaData.input[key] ?? '-';
                } else if (slaData.input[key] !== undefined && slaData.input[key] !== null) {
                    filteredInput[key] = slaData.input[key];
                }
            }
        });
        if (Object.keys(filteredInput).length > 0) {
            filteredData.input = filteredInput;
        }
    }

    // Filter calculation object
    if (slaData.calculation) {
        const filteredCalc: any = {};
        const calcKeys = ['baseDate', 'pragueTime', 'isBeforeCutTime', 'isHolidayOrWeekend', 'nextWorkingDate'];
        calcKeys.forEach(key => {
            const fullKey = `SLA_CALCULATION.calculation.${key}`;
            if (isKeyAllowed(fullKey) && slaData.calculation[key] !== undefined && slaData.calculation[key] !== null) {
                filteredCalc[key] = slaData.calculation[key];
            }
        });
        if (Object.keys(filteredCalc).length > 0) {
            filteredData.calculation = filteredCalc;
        }
    }

    return filteredData;
};

function createMapByKey<T, K extends keyof T>(array: T[], key: K): Map<T[K], T> {
    const map = new Map<T[K], T>();
    array.forEach((item) => {
        const keyValue = item[key];
        map.set(keyValue, item);
    });
    return map;
}

const isNullEmpty = (value: any) => value === '' || value === null;

export interface HistoryPropsType {
    changelog: OrderChangelogType | null;
    orderStatusCodesObject: ConstantsCodesObjectType;
    orderMarketplacesCodesObject: ConstantsCodesObjectType;
    orderCancelReasonCodesObject: ConstantsCodesObjectType;
    paymentCodesObject: ConstantsCodesObjectType;
    shipperCodesObject: ConstantsCodesObjectType;
    serviceCodesObject: ConstantsCodesObjectType;
    lineStateCodesObject: ConstantsCodesObjectType;
    orderProductCustomsTypeCodesObject: ConstantsCodesObjectType;
    orderProductCustomizationTypeCodesObject: ConstantsCodesObjectType;
    boolNumberCodesObject: ConstantsCodesObjectType;
    orderTypeCodesObject: ConstantsCodesObjectType;
    sourceSysCodesObject: ConstantsCodesObjectType;
    productsForSets: OrderProductSetWithProductType[];
    skladonUser?: number;
}

const HistoryBlock: FC<HistoryPropsType> = (props) => {
    const [updates, setUpdates] = useState<ChangelogUpdateType[]>([]);
    const countryCodesObject = getCodesObject(getCountryOptions());
    const palletTypes = useSelector((state: RootStoreType) => state.orderDetail.palletTypes);
    useEffect(() => {
        if (props.changelog?.updates) {
            const productsSetsIdsMap = props.productsForSets.length
                ? createMapByKey(props.productsForSets, 'ID_SET')
                : new Map();

            setUpdates(
                props.changelog.updates
                    .map(resolveHistoryMessage)
                    .map((update) => {
                        if (!update.data) {
                            return update;
                        }

                        const newData = { ...update.data } as any;
                        if (newData?.ORDER_PRODUCT_SETS) {
                            newData.ORDER_PRODUCT_SETS = newData?.ORDER_PRODUCT_SETS?.filter(
                                (itemSet: any) => !!itemSet
                            ).map((itemSet: any) => {
                                return {
                                    ...itemSet,
                                    SET_SKU: productsSetsIdsMap.get(itemSet?.ID_SET)?.SKU,
                                    PRODUCT_PRICE: itemSet.ORIGINAL_PRODUCT_PRICE
                                };
                            });
                        }
                        if (newData?.ORDER_PRODUCTS) {
                            newData.ORDER_PRODUCTS = newData?.ORDER_PRODUCTS?.filter(
                                (itemProduct: any) => !!itemProduct
                            ).map((itemProduct: any) => {
                                return {
                                    ...itemProduct,
                                    SET_SKU: productsSetsIdsMap.get(itemProduct?.ID_SET)?.SKU
                                };
                            });
                        }

                        if (newData.ORDER_PRODUCT_SETS) {
                            newData.ORDER_PRODUCTS = newData.ORDER_PRODUCTS?.filter((orderProduct: any) => {
                                if (orderProduct.ID_SET) {
                                    const set = newData.ORDER_PRODUCT_SETS.find(
                                        (set: any) => set.ID_SET === orderProduct.ID_SET
                                    );
                                    if (set) {
                                        if (set.changes) {
                                            set.changes = { ...set.changes };
                                            if (set.changes.ORDER_PRODUCTS) {
                                                set.changes.ORDER_PRODUCTS.push(orderProduct);
                                            } else {
                                                set.changes.ORDER_PRODUCTS = [orderProduct];
                                            }
                                        } else {
                                            if (set.ORDER_PRODUCTS) {
                                                set.ORDER_PRODUCTS.push(orderProduct);
                                            } else {
                                                set.ORDER_PRODUCTS = [orderProduct];
                                            }
                                        }
                                        return false;
                                    }
                                }
                                return true;
                            });
                        }
                        if (
                            newData.TRACKING_NUMBER &&
                            typeof newData.TRACKING_NUMBER == 'object' &&
                            isNullEmpty(newData.TRACKING_NUMBER.new) &&
                            isNullEmpty(newData.TRACKING_NUMBER.old)
                        ) {
                            delete newData.TRACKING_NUMBER;
                        }
                        // Filter and translate SLA_CALCULATION data based on reason type
                        if (newData.SLA_CALCULATION) {
                            const reason = newData.SLA_CALCULATION.reason;
                            // First filter to only relevant attributes for this reason
                            let filteredSla = filterSlaDataByReason(newData.SLA_CALCULATION, reason);
                            
                            // Format result date for the heading/title (used in message template)
                            // Handle both valid date and null (cleared SLA)
                            if (filteredSla.result) {
                                filteredSla = {
                                    ...filteredSla,
                                    resultMessage: `${translate('orderDetail.slaCalculation.calculated')}: ${moment(filteredSla.result).format('LLL')}`
                                };
                            } else if (filteredSla.result === null) {
                                filteredSla = {
                                    ...filteredSla,
                                    resultMessage: translate('orderDetail.slaCalculation.cleared')
                                };
                            }
                            
                            // Translate orderType using orderTypeCodesObject
                            if (filteredSla.input?.orderType) {
                                const orderTypeKey = filteredSla.input.orderType.toLowerCase();
                                const translatedOrderType = props.orderTypeCodesObject[orderTypeKey]?.label;
                                if (translatedOrderType) {
                                    filteredSla = {
                                        ...filteredSla,
                                        input: {
                                            ...filteredSla.input,
                                            orderType: translatedOrderType
                                        }
                                    };
                                }
                            }
                            
                            // Then translate boolean values if calculation exists
                            if (filteredSla.calculation) {
                                filteredSla = {
                                    ...filteredSla,
                                    calculation: {
                                        ...filteredSla.calculation,
                                        isBeforeCutTime: filteredSla.calculation.isBeforeCutTime !== undefined 
                                            ? translateBoolean(filteredSla.calculation.isBeforeCutTime) 
                                            : undefined,
                                        isHolidayOrWeekend: filteredSla.calculation.isHolidayOrWeekend !== undefined 
                                            ? translateBoolean(filteredSla.calculation.isHolidayOrWeekend) 
                                            : undefined
                                    }
                                };
                                // Clean up undefined values
                                if (filteredSla.calculation.isBeforeCutTime === undefined) {
                                    delete filteredSla.calculation.isBeforeCutTime;
                                }
                                if (filteredSla.calculation.isHolidayOrWeekend === undefined) {
                                    delete filteredSla.calculation.isHolidayOrWeekend;
                                }
                            }
                            newData.SLA_CALCULATION = filteredSla;
                        }
                        return { ...update, data: newData };
                    })
                    .filter((update) => update.data && Object.keys(update.data).length)
            );
        }
    }, [props.changelog, props.productsForSets]);

    return (
        <History
            updateKeys={[
                ...(props.skladonUser ? historyUpdatesSkladonUser : historyUpdates),
                ...historySlaCalculationUpdates,
            ]}
            updates={updates}
            updateItemsKeys={{
                ORDER_PRODUCTS: {
                    requiredItems: ['PRODUCT.NAME', 'SET_SKU', 'PRODUCT.SKU'],
                    itemKeys: historyProductUpdates
                },
                ORDER_FILES: {
                    requiredItems: ['FILE.ORIGINAL_FILE_NAME', 'FILE.FILE_TYPE', 'FILE.TYPE'],
                    itemKeys: historyOrderFileUpdates
                },
                ORDER_PRODUCT_FILES: {
                    requiredItems: ['PRODUCT.NAME', 'FILE.ORIGINAL_FILE_NAME', 'FILE.FILE_TYPE', 'FILE.TYPE'],
                    itemKeys: historyOrderProductFileUpdates
                },
                ORDER_PRODUCT_SETS: { requiredItems: ['SET_SKU'], itemKeys: historyProductSetsUpdates },
                ORDER_PALLETS: { requiredItems: ['PALLET_TYPE'], itemKeys: historyOrderPalletUpdates }
            }}
            keyCodes={{
                ORDER_TYPE: props.orderTypeCodesObject,
                STATUS: props.orderStatusCodesObject,
                MARKETPLACE: props.orderMarketplacesCodesObject,
                CANCEL_REASON: props.orderCancelReasonCodesObject,
                PAYMENT_METHOD: props.paymentCodesObject,
                SHIPPER: props.shipperCodesObject,
                SHIPPER_SERVICE_CODE: props.serviceCodesObject,
                PAID: props.boolNumberCodesObject,
                PRINT_FILES: props.boolNumberCodesObject,
                DISABLE_SHIPPING_REORDER: props.boolNumberCodesObject,
                ALLOW_EXPEDITION: props.boolNumberCodesObject,
                TRANSPORT_PAID_BY_SKLADON: props.boolNumberCodesObject,
                SHIPMENT_PENDING: props.boolNumberCodesObject,
                BILLING_STATE: countryCodesObject,
                RECIPIENT_STATE: countryCodesObject,
                PRINT_RETURN_LABEL: props.boolNumberCodesObject,
                HOLD_BY_QUALITY: props.boolNumberCodesObject,
                PICK_FROM_CUSTOMS_WAREHOUSE: props.boolNumberCodesObject,
                UNDER_SANCTION: props.boolNumberCodesObject,
                SHIPPER_LABEL_PRINTED: props.boolNumberCodesObject,
                SOURCE_SYS: props.sourceSysCodesObject,
            }}
            keyItemsCodes={{
                ORDER_PRODUCTS: {
                    LINE_STATE: props.lineStateCodesObject,
                    CUSTOMS_PRODUCT_TYPE: props.orderProductCustomsTypeCodesObject,
                    CUSTOMIZATION_TYPE: props.orderProductCustomizationTypeCodesObject
                },
                ORDER_PRODUCT_SETS: {
                    LINE_STATE: props.lineStateCodesObject
                }
            }}
            translatePrefix="orderDetail"
            createHiddenKeys={historyCreateHiddenKeys}
        />
    );
};

export default HistoryBlock;
